'use client';

import { Metadata } from 'next';
import Image from 'next/image';
import Link from 'next/link';
import { useState } from 'react';
import InnerPageHeader from '@/components/InnerPageHeader';
import HomepageFooter from '@/components/HomepageFooter';
import EnquiryModal from '@/components/EnquiryModal';
import '@/styles/global.css';

interface StudioPageProps {
  params: {
    slug: string;
  };
}

export default function StudioDetailPage({ params }: StudioPageProps) {
  const [activeTab, setActiveTab] = useState('overview');
  const [showFullContent, setShowFullContent] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const handleTabClick = (tab: string) => {
    setActiveTab(tab);
  };

  const toggleContent = () => {
    setShowFullContent(!showFullContent);
  };

  const openModal = () => {
    setShowModal(true);
  };

  const closeModal = () => {
    // Add close animation classes
    const overlay = document.querySelector('.animate-fade-in');
    const modal = document.querySelector('.animate-slide-up');
    
    if (overlay) {
      overlay.classList.remove('animate-fade-in');
      overlay.classList.add('animate-fade-out');
    }
    
    if (modal) {
      modal.classList.remove('animate-slide-up');
      modal.classList.add('animate-slide-down');
    }
    
    // Hide modal after animation
    setTimeout(() => {
      setShowModal(false);
    }, 300);
  };

  return (
    <div className="min-h-screen bg-[#f6efed]">
      <InnerPageHeader />
      
      <main className="detail-page-studio">
        {/* Gallery Section */}
        <section className="bg-[#2E2A3A] p-[10px] relative">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-[10px]">
            {/* Large Image - Takes 2 columns and 2 rows */}
            <div className="col-span-2 row-span-2 relative h-[780px] overflow-hidden group cursor-pointer bg-[#2E2A3A] transition-all duration-500 ease-in-out group-hover:shadow-[0_20px_40px_rgba(0,0,0,0.3)] group-hover:z-10 group-hover:translate-y-[-4px]">
              <Image
                src="/gallery1.png"
                alt="Studio Main View"
                fill
                className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105 group-hover:brightness-110 transform-gpu"
              />
            </div>

            {/* Top Right Image */}
            <div className="col-span-2 relative h-[385px] overflow-hidden group cursor-pointer bg-[#2E2A3A] transition-all duration-500 ease-in-out group-hover:shadow-[0_20px_40px_rgba(0,0,0,0.3)] group-hover:z-10 group-hover:translate-y-[-4px]">
              <Image
                src="/gallery2.jpg"
                alt="Studio Equipment"
                fill
                className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105 group-hover:brightness-110 transform-gpu"
              />
            </div>

            {/* Bottom Right - 2 Small Images */}
            <div className="relative h-[385px] overflow-hidden group cursor-pointer bg-[#2E2A3A] transition-all duration-500 ease-in-out group-hover:shadow-[0_20px_40px_rgba(0,0,0,0.3)] group-hover:z-10 group-hover:translate-y-[-4px]">
              <Image
                src="/gallery3.jpg"
                alt="Studio Class"
                fill
                className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105 group-hover:brightness-110 transform-gpu"
              />
            </div>

            <div className="relative h-[385px] overflow-hidden group cursor-pointer bg-[#2E2A3A] transition-all duration-500 ease-in-out group-hover:shadow-[0_20px_40px_rgba(0,0,0,0.3)] group-hover:z-10 group-hover:translate-y-[-4px]">
              <Image
                src="/gallery4.jpg"
                alt="Studio Member"
                fill
                className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105 group-hover:brightness-110 transform-gpu"
              />
            </div>
          </div>
          
          {/* Show All Photos Button - Separate */}
          <div className="absolute bottom-[30px] right-[30px] z-20">
            <button className="px-6 py-3 bg-white text-[#8A7296] rounded-[50px] font-['Archivo',sans-serif] text-[18px] font-medium flex items-center gap-2 hover:bg-[#FBEFF9] transition-all duration-300 shadow-lg hover:shadow-xl">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              Show All Photos
            </button>
          </div>
        </section>

        {/* Studio Details Section */}
        <section className="py-20 max-w-[1490px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="md:col-span-2 space-y-8 md:pr-[100px]">
              {/* Tags Section */}
              <div className="studio-tags flex items-center gap-3 flex-wrap">
                  <span className="studio-tag px-3 py-1 rounded-[5px] font-['Archivo',sans-serif] text-[15px] flex items-center gap-1">
                    <Image src="/women-only-icon.svg" width={12} height={12} alt="Women-Only" />
                    Women-Only
                  </span>
                  <span className="studio-tag px-3 py-1 rounded-[5px] font-['Archivo',sans-serif] text-[15px] flex items-center gap-1">
                    <Image src="/Women-Led.png" width={12} height={12} alt="Women-Led" />
                    Women-Led
                  </span>
                  <span className="studio-tag px-3 py-1 rounded-[5px] font-['Archivo',sans-serif] text-[15px] flex items-center gap-1">
                    <Image src="/Verified by FFF.svg" width={12} height={12} alt="Verified by FFF" />
                    Verified by FFF
                  </span>
              </div>

              {/* Studio Hero Section */}
              <div className="studio-hero">
                <div className="studio-container">
                  <h1 className="font-['Gotham_Rounded',sans-serif] text-[32px] font-bold text-[#2E2A3A] mb-4">
                    Empowered Gyms Bromley Online
                  </h1>

                  <div className="meta flex flex-wrap items-center gap-4 mb-6">
                    <div className="rating flex items-center gap-2">
                      <span className="stars text-[#FFA500] text-[16px]">★★★★★</span>
                      <span className="score font-['Archivo',sans-serif] text-[14px] font-bold text-[#2E2A3A]">4.9</span>
                      <span className="reviews font-['Archivo',sans-serif] text-[14px] text-[#6e6672]">(138 reviews)</span>
                    </div>

                    <span className="dot text-[#6e6672]">•</span>

                    <div className="location flex items-center gap-1">
                      <i className="fas fa-map-marker-alt text-[18px] opacity-100"></i>
                      <span className="font-['Archivo',sans-serif] text-[14px] text-[#2E2A3A]">Islington, London · 0.8 miles away</span>
                    </div>

                    <span className="dot text-[#6e6672]">•</span>

                    <div className="hours flex items-center gap-1">
                      <i className="fas fa-clock text-[18px] opacity-100"></i>
                      <span className="font-['Archivo',sans-serif] text-[14px] text-[#2E2A3A]">Open now · Until 9:30 PM</span>
                    </div>
                  </div>

                  <p className="description font-['Archivo',sans-serif] text-[16px] text-[#6e6672] leading-[26px]">
                    A serene women-only yoga and mindfulness studio nestled in the heart of Islington.
                    Founded by women, for women - every class, every space, every detail is designed with
                    your comfort and safety in mind.
                  </p>
                </div>
              </div>

              {/* Studio Tabs Section */}
              <div className="studio-tabs">
                <nav className="tabs-nav flex items-center gap-8 mb-8 border-b border-[#e5e5e5]">
                  <button 
                    onClick={() => handleTabClick('overview')}
                    className={`pb-4 font-['Archivo',sans-serif] font-medium text-[16px] border-b-2 transition-colors ${
                      activeTab === 'overview' 
                        ? 'text-[#796080] border-[#796080]' 
                        : 'text-[#6e6672] border-transparent hover:text-[#796080]'
                    }`}
                  >
                    Overview
                  </button>
                  <button 
                    onClick={() => handleTabClick('pricing')}
                    className={`pb-4 font-['Archivo',sans-serif] font-medium text-[16px] border-b-2 transition-colors ${
                      activeTab === 'pricing' 
                        ? 'text-[#796080] border-[#796080]' 
                        : 'text-[#6e6672] border-transparent hover:text-[#796080]'
                    }`}
                  >
                    Pricing
                  </button>
                  <button 
                    onClick={() => handleTabClick('reviews')}
                    className={`pb-4 font-['Archivo',sans-serif] font-medium text-[16px] border-b-2 transition-colors ${
                      activeTab === 'reviews' 
                        ? 'text-[#796080] border-[#796080]' 
                        : 'text-[#6e6672] border-transparent hover:text-[#796080]'
                    }`}
                  >
                    Reviews
                  </button>
                  <button 
                    onClick={() => handleTabClick('location')}
                    className={`pb-4 font-['Archivo',sans-serif] font-medium text-[16px] border-b-2 transition-colors ${
                      activeTab === 'location' 
                        ? 'text-[#796080] border-[#796080]' 
                        : 'text-[#6e6672] border-transparent hover:text-[#796080]'
                    }`}
                  >
                    Location
                  </button>
                </nav>

                <div className="tab-content">
                  {activeTab === 'overview' && (
                    <div>
                      <h2 className="font-['Gotham_Rounded',sans-serif] text-[28px] font-bold text-[#2E2A3A] mb-6">
                        About the Studio
                      </h2>

                      <div className="space-y-4">
                        <p className="font-['Archivo',sans-serif] text-[16px] text-[#6e6672] leading-[28px]">
                          Serenity Studio was born from a simple belief: every woman deserves a space
                          where she feels completely safe, seen, and celebrated. Founded in 2018 by Amara Osei
                          and Priya Sharma - a yoga teacher and a physiotherapist - the studio was designed from
                          the ground up as a refuge from the male-dominated fitness world.
                        </p>

                        {showFullContent && (
                          <p className="font-['Archivo',sans-serif] text-[16px] text-[#6e6672] leading-[28px]">
                            Our 2,400 sq ft studio features floor-to-ceiling windows overlooking our private
                            courtyard garden, with fully covered glazing for complete privacy during classes. We offer
                            everything from restorative yin yoga to dynamic vinyasa flow, guided by our team of
                            seven female instructors - each with over 500 hours of certified training. The studio is
                            fully accessible, eco-certified, and designed to welcome women of all backgrounds,
                            faiths, and fitness levels.
                          </p>
                        )}
                      </div>

                      <button 
                        onClick={toggleContent}
                        className="show-less mt-6 text-[#796080] font-['Archivo',sans-serif] font-medium text-[16px] hover:text-[#6a5a70] transition-colors flex items-center gap-1"
                      >
                        {showFullContent ? 'Show Less ‹' : 'Show More ›'}
                      </button>
                    </div>
                  )}

                  {activeTab === 'pricing' && (
                    <div>
                      <h2 className="font-['Gotham_Rounded',sans-serif] text-[28px] font-bold text-[#2E2A3A] mb-6">
                        Pricing Plans
                      </h2>
                      <p className="font-['Archivo',sans-serif] text-[16px] text-[#6e6672] leading-[28px]">
                        Pricing information will be displayed here.
                      </p>
                    </div>
                  )}

                  {activeTab === 'reviews' && (
                    <div>
                      <h2 className="font-['Gotham_Rounded',sans-serif] text-[28px] font-bold text-[#2E2A3A] mb-6">
                        Reviews
                      </h2>
                      <p className="font-['Archivo',sans-serif] text-[16px] text-[#6e6672] leading-[28px]">
                        Reviews will be displayed here.
                      </p>
                    </div>
                  )}

                  {activeTab === 'location' && (
                    <div>
                      <h2 className="font-['Gotham_Rounded',sans-serif] text-[28px] font-bold text-[#2E2A3A] mb-6">
                        Location
                      </h2>
                      <p className="font-['Archivo',sans-serif] text-[16px] text-[#6e6672] leading-[28px]">
                        Location information will be displayed here.
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Separator */}
              <div className="py-[50px]">
                <div className="border-b border-black opacity-10"></div>
              </div>

              {/* What We Offer Section */}
              <div className="offer-section">
                <h2 className="font-['Gotham_Rounded',sans-serif] text-[32px] font-bold text-[#2E2A3A] mb-8">
                  What We Offer
                </h2>

                <div className="offer-grid grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  <div className="offer-card bg-white rounded-[16px] p-6 text-left">
                    <div className="icon mb-4">
                      <Image src="/Yoga Classes.svg" width={50} height={50} alt="Yoga Classes" />
                    </div>
                    <h3 className="font-['Gotham_Rounded',sans-serif] text-[20px] font-bold text-[#2E2A3A] mb-2">Yoga Classes</h3>
                    <p className="font-['Archivo',sans-serif] text-[16px] text-[#6e6672]">Vinyasa, Yin, Restorative, Prenatal</p>
                  </div>

                  <div className="offer-card bg-white rounded-[16px] p-6 text-left">
                    <div className="icon mb-4">
                      <Image src="/Pilates.svg" width={50} height={50} alt="Pilates" />
                    </div>
                    <h3 className="font-['Gotham_Rounded',sans-serif] text-[20px] font-bold text-[#2E2A3A] mb-2">Pilates</h3>
                    <p className="font-['Archivo',sans-serif] text-[16px] text-[#6e6672]">Mat & Reformer · All levels</p>
                  </div>

                  <div className="offer-card bg-white rounded-[16px] p-6 text-left">
                    <div className="icon mb-4">
                      <Image src="/Breathwork.svg" width={50} height={50} alt="Breathwork" />
                    </div>
                    <h3 className="font-['Gotham_Rounded',sans-serif] text-[20px] font-bold text-[#2E2A3A] mb-2">Breathwork</h3>
                    <p className="font-['Archivo',sans-serif] text-[16px] text-[#6e6672]">Pranayama & Meditation</p>
                  </div>

                  <div className="offer-card bg-white rounded-[16px] p-6 text-left">
                    <div className="icon mb-4">
                      <Image src="/1-to-1 Training.svg" width={50} height={50} alt="1-to-1 Training" />
                    </div>
                    <h3 className="font-['Gotham_Rounded',sans-serif] text-[20px] font-bold text-[#2E2A3A] mb-2">1-to-1 Training</h3>
                    <p className="font-['Archivo',sans-serif] text-[16px] text-[#6e6672]">Private sessions with instructors</p>
                  </div>

                  <div className="offer-card bg-white rounded-[16px] p-6 text-left">
                    <div className="icon mb-4">
                      <Image src="/Prenatal Yoga.svg" width={50} height={50} alt="Prenatal Yoga" />
                    </div>
                    <h3 className="font-['Gotham_Rounded',sans-serif] text-[20px] font-bold text-[#2E2A3A] mb-2">Prenatal Yoga</h3>
                    <p className="font-['Archivo',sans-serif] text-[16px] text-[#6e6672]">All trimesters · Specialist led</p>
                  </div>

                  <div className="offer-card bg-white rounded-[16px] p-6 text-left">
                    <div className="icon mb-4">
                      <Image src="/Workshops.svg" width={50} height={50} alt="Workshops" />
                    </div>
                    <h3 className="font-['Gotham_Rounded',sans-serif] text-[20px] font-bold text-[#2E2A3A] mb-2">Workshops</h3>
                    <p className="font-['Archivo',sans-serif] text-[16px] text-[#6e6672]">Monthly events & retreats</p>
                  </div>
                </div>
              </div>

              {/* Separator */}
              <div className="py-[50px]">
                <div className="border-b border-black opacity-10"></div>
              </div>

              {/* Safety & Comfort Section */}
              <div className="safety-section">
                <h2 className="font-['Gotham_Rounded',sans-serif] text-[32px] font-bold text-[#2E2A3A] mb-8">
                  Safety & Comfort
                </h2>

                <div className="safety-grid grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="safety-card flex items-start gap-4">
                    <div className="icon flex-shrink-0">
                      <Image src="/Women-Only Policy.svg" width={40} height={40} alt="Women-Only Policy" />
                    </div>
                    <div>
                      <h3 className="font-['Gotham_Rounded',sans-serif] text-[20px] font-bold text-[#2E2A3A] mb-2">Women-Only Policy</h3>
                      <p className="font-['Archivo',sans-serif] text-[16px] text-[#6e6672]">All staff and clients are women. Door policy enforced at all times, no exceptions.</p>
                    </div>
                  </div>

                  <div className="safety-card flex items-start gap-4">
                    <div className="icon flex-shrink-0">
                      <Image src="/Fully Accessible.svg" width={40} height={40} alt="Fully Accessible" />
                    </div>
                    <div>
                      <h3 className="font-['Gotham_Rounded',sans-serif] text-[20px] font-bold text-[#2E2A3A] mb-2">Fully Accessible</h3>
                      <p className="font-['Archivo',sans-serif] text-[16px] text-[#6e6672]">Step-free access, accessible changing rooms, and adaptive class options available.</p>
                    </div>
                  </div>

                  <div className="safety-card flex items-start gap-4">
                    <div className="icon flex-shrink-0">
                      <Image src="/Complete Privacy.svg" width={40} height={40} alt="Complete Privacy" />
                    </div>
                    <div>
                      <h3 className="font-['Gotham_Rounded',sans-serif] text-[20px] font-bold text-[#2E2A3A] mb-2">Complete Privacy</h3>
                      <p className="font-['Archivo',sans-serif] text-[16px] text-[#6e6672]">All windows frosted or covered. No external visibility into any studio or changing space.</p>
                    </div>
                  </div>

                  <div className="safety-card flex items-start gap-4">
                    <div className="icon flex-shrink-0">
                      <Image src="/FFF Verified.svg" width={40} height={40} alt="FFF Verified" />
                    </div>
                    <div>
                      <h3 className="font-['Gotham_Rounded',sans-serif] text-[20px] font-bold text-[#2E2A3A] mb-2">FFF Verified</h3>
                      <p className="font-['Archivo',sans-serif] text-[16px] text-[#6e6672]">Independently reviewed and approved by our Female Fitness Finder safety team.</p>
                    </div>
                  </div>
                </div>
              </div>

                          </div>

            {/* Sidebar */}
            <div className="sidebar space-y-8">
              {/* Top Button */}
              <button 
                onClick={openModal}
                className="w-full px-6 py-4 bg-[#8A7296] hover:bg-[#7a647d] text-white rounded-[50px] transition-colors font-['Archivo',sans-serif] text-[16px] font-medium flex items-center justify-center gap-2"
              >
                Send a message
                <Image src="/btn-arrow.svg" width={12} height={12} alt="Arrow" />
              </button>

              {/* Pricing Card */}
              <div className="bg-white rounded-[24px] p-[30px] relative">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="font-['Archivo',sans-serif] text-[18px] font-medium text-[#796080]">
                    Monthly
                  </h3>
                  <div className="inline-block px-[28px] py-[8px] bg-[#796080] rounded-full">
                    <span className="font-['Archivo',sans-serif] text-[14px] text-white font-medium">
                      Best Value
                    </span>
                  </div>
                </div>
                
                <div className="text-left mb-6">
                  <div className="text-[42px] font-medium text-[#2E2A3A] mb-2 font-['Gotham_Rounded',sans-serif]">
                    £69.00
                  </div>
                  <p className="font-['Archivo',sans-serif] text-[18px] text-[#000000B3] font-normal">
                    per month · unlimited classes
                  </p>
                </div>

                <ul className="space-y-4 mb-6 list-none p-0 m-0">
                  <li className="flex items-start gap-3">
                    <svg className="w-[27px] h-[27px] text-[#8A7296]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <circle cx="12" cy="12" r="8" strokeWidth="2"/>
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4" />
                    </svg>
                    <span className="font-['Archivo',sans-serif] text-[18px] text-[#000000B3] font-normal">Unlimited Classes</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <svg className="w-[27px] h-[27px] text-[#8A7296]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <circle cx="12" cy="12" r="8" strokeWidth="2"/>
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4" />
                    </svg>
                    <span className="font-['Archivo',sans-serif] text-[18px] text-[#000000B3] font-normal">All Class Types</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <svg className="w-[27px] h-[27px] text-[#8A7296]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <circle cx="12" cy="12" r="8" strokeWidth="2"/>
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4" />
                    </svg>
                    <span className="font-['Archivo',sans-serif] text-[18px] text-[#000000B3] font-normal">Priority Booking</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <svg className="w-[27px] h-[27px] text-[#8A7296]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <circle cx="12" cy="12" r="8" strokeWidth="2"/>
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4" />
                    </svg>
                    <span className="font-['Archivo',sans-serif] text-[18px] text-[#000000B3] font-normal">Guest Pass Monthly</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <svg className="w-[27px] h-[27px] text-[#8A7296]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <circle cx="12" cy="12" r="8" strokeWidth="2"/>
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4" />
                    </svg>
                    <span className="font-['Archivo',sans-serif] text-[18px] text-[#000000B3] font-normal">10% Off Workshops</span>
                  </li>
                </ul>

                <div className="">
                  <p className="font-['Archivo',sans-serif] text-[18px] text-[#6e6672] text-left">
                    All prices include mat hire &amp; refreshments.
                  </p>
                  <p className="font-['Archivo',sans-serif] text-[18px] text-[#6e6672] text-left">
                    First class free for new members.
                  </p>
                </div>

                              </div>

              {/* Contact Studio */}
              <div className="bg-white rounded-[24px] p-[30px]">
                <h3 className="font-['Gotham_Rounded',sans-serif] text-[24px] font-bold text-[#2E2A3A] mb-6">
                  Contact Studio
                </h3>
                <div className="space-y-4">
                  <a href="#" className="flex items-center gap-4 p-4 border border-[#d7d7d7] rounded-[10px] no-underline text-inherit bg-white hover:bg-[#FBEFF9] transition-colors">
                    <Image src="/Send a message.svg" width={30} height={30} alt="Send a message" />
                    <div>
                      <strong className="block text-[18px] font-medium text-[#2E2A3A] mb-1">Send a message</strong>
                      <small className="block text-[14px] text-[#6e6672]">Usually responds within 2 hours</small>
                    </div>
                  </a>

                  <a href="tel:02079460845" className="flex items-center gap-4 p-4 border border-[#d7d7d7] rounded-[10px] no-underline text-inherit bg-white hover:bg-[#FBEFF9] transition-colors">
                    <Image src="/Call the studio.svg" width={30} height={30} alt="Call the studio" />
                    <div>
                      <strong className="block text-[18px] font-medium text-[#2E2A3A] mb-1">Call the studio</strong>
                      <small className="block text-[14px] text-[#6e6672]">020 7946 0845 · Open now</small>
                    </div>
                  </a>

                  <a href="https://serenitystudiolondon.co.uk" className="flex items-center gap-4 p-4 border border-[#d7d7d7] rounded-[10px] no-underline text-inherit bg-white hover:bg-[#FBEFF9] transition-colors">
                    <Image src="/Visit website.svg" width={30} height={30} alt="Visit website" />
                    <div>
                      <strong className="block text-[18px] font-medium text-[#2E2A3A] mb-1">Visit website</strong>
                      <small className="block text-[14px] text-[#6e6672]">serenitystudiolondon.co.uk</small>
                    </div>
                  </a>
                </div>
              </div>

              {/* Quick Facts */}
              <div className="bg-white rounded-[24px] p-[30px]">
                <h3 className="font-['Gotham_Rounded',sans-serif] text-[24px] font-bold text-[#2E2A3A] mb-6">
                  Quick Facts
                </h3>
                <ul className="space-y-4 list-none p-0 m-0">
                  <li className="flex items-center gap-4">
                    <span className="w-7 h-7 flex items-center justify-center">
                      <Image src="/fact1.svg" width={24} height={24} alt="Women-only" />
                    </span>
                    <span className="font-['Archivo',sans-serif] text-[14px] text-[#2E2A3A]">Women-only · Est. 2018 · Founded by women</span>
                  </li>
                  <li className="flex items-center gap-4">
                    <span className="w-7 h-7 flex items-center justify-center">
                      <Image src="/fact2.svg" width={24} height={24} alt="Space" />
                    </span>
                    <span className="font-['Archivo',sans-serif] text-[14px] text-[#2E2A3A]">2,400 sq ft · Private courtyard garden</span>
                  </li>
                  <li className="flex items-center gap-4">
                    <span className="w-7 h-7 flex items-center justify-center">
                      <Image src="/fact3.svg" width={24} height={24} alt="Verified" />
                    </span>
                    <span className="font-['Archivo',sans-serif] text-[14px] text-[#2E2A3A]">FFF Verified · DBS-checked instructors</span>
                  </li>
                  <li className="flex items-center gap-4">
                    <span className="w-7 h-7 flex items-center justify-center">
                      <Image src="/fact4.svg" width={24} height={24} alt="Instructors" />
                    </span>
                    <span className="font-['Archivo',sans-serif] text-[14px] text-[#2E2A3A]">7 female instructors · 500+ hrs certified</span>
                  </li>
                  <li className="flex items-center gap-4">
                    <span className="w-7 h-7 flex items-center justify-center">
                      <Image src="/fact5.svg" width={24} height={24} alt="Class size" />
                    </span>
                    <span className="font-['Archivo',sans-serif] text-[14px] text-[#2E2A3A]">Classes capped at 12 for quality</span>
                  </li>
                </ul>
              </div>

                          </div>
          </div>
        </section>

              </main>

      <EnquiryModal showModal={showModal} closeModal={closeModal} />

      <HomepageFooter />
    </div>
  );
}
